import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV_SERVICES, NAV_INDUSTRIES } from "../lib/data";

const AT_TOP_THRESHOLD = 80;   // px — below this = "at top"
const HIDE_THRESHOLD   = 12;   // px scrolled down before hiding

export default function Navbar() {
  const [atTop,  setAtTop]  = useState(true);   // transparent when true
  const [hidden, setHidden] = useState(false);  // slide out when true
  const [open,   setOpen]   = useState(false);
  const lastY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y    = window.scrollY;
      const diff = y - lastY.current;

      // ── At-top state (transparent bg) ──
      setAtTop(y < AT_TOP_THRESHOLD);

      // ── Hide / show logic ──
      if (y < AT_TOP_THRESHOLD) {
        // Always show at the top
        setHidden(false);
      } else if (diff > HIDE_THRESHOLD) {
        // Scrolling DOWN → hide
        setHidden(true);
        if (open) setOpen(false); // close mobile menu too
      } else if (diff < -HIDE_THRESHOLD) {
        // Scrolling UP → reveal
        setHidden(false);
      }

      lastY.current = y;
    };

    // Initialise on mount
    lastY.current = window.scrollY;
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const navClass = [
    "navbar",
    atTop   ? "at-top"  : "",
    hidden  ? "nav-hidden" : "",
  ].filter(Boolean).join(" ");

  return (
    <>
      <nav className={navClass} data-testid="navbar">
        <div className="container-x navbar-inner">
          <Link to="/" className="nav-brand" data-testid="nav-brand">
            <img src="/logo.png" alt="Sinix Studio" style={{ height: 26, width: "auto", display: "block" }} />
            <span>Sinix Studio</span>
          </Link>

          <div className="nav-links">
            <Link to="/work" className={`nav-link ${isActive("/work") ? "active" : ""}`} data-testid="nav-work">Work</Link>
            <Link to="/about" className={`nav-link ${isActive("/about") ? "active" : ""}`} data-testid="nav-about">About</Link>

            <div className="nav-dropdown-wrapper">
              <Link to="/services" className={`nav-link ${isActive("/services") ? "active" : ""}`} data-testid="nav-services-trigger">
                Services <ChevronDown size={14} />
              </Link>
              <div className="nav-dropdown" role="menu">
                <div className="dd-label">// All Services</div>
                <Link to="/services" data-testid="nav-services-all">Services Overview</Link>
                <div className="dd-label" style={{ marginTop: 6 }}>// Practices</div>
                {NAV_SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`nav-service-${s.slug}`}>
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="nav-dropdown-wrapper">
              <Link to="/industries" className={`nav-link ${isActive("/industries") ? "active" : ""}`} data-testid="nav-industries-trigger">
                Industries <ChevronDown size={14} />
              </Link>
              <div className="nav-dropdown" role="menu">
                <div className="dd-label">// Overview</div>
                <Link to="/industries" data-testid="nav-industries-all">Industries Overview</Link>
                <div className="dd-label" style={{ marginTop: 6 }}>// Sectors</div>
                {NAV_INDUSTRIES.map((s) => (
                  <Link key={s.slug} to={`/industries/${s.slug}`} data-testid={`nav-industry-${s.slug}`}>
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/process" className={`nav-link ${isActive("/process") ? "active" : ""}`} data-testid="nav-process">Process</Link>
            <Link to="/contact" className="nav-cta" data-testid="nav-contact-cta">
              Contact <ArrowUpRight size={14} />
            </Link>
          </div>

          <button className="nav-burger" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-burger">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="mobile-drawer" data-testid="mobile-drawer">
          <Link to="/work" data-testid="m-nav-work">Work</Link>
          <Link to="/about" data-testid="m-nav-about">About</Link>
          <div className="mobile-section-title">// Services</div>
          <Link to="/services" data-testid="m-nav-services">All Services</Link>
          {NAV_SERVICES.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`m-nav-service-${s.slug}`}>
              {s.label}
            </Link>
          ))}
          <div className="mobile-section-title">// Industries</div>
          <Link to="/industries" data-testid="m-nav-industries">All Industries</Link>
          {NAV_INDUSTRIES.map((s) => (
            <Link key={s.slug} to={`/industries/${s.slug}`} data-testid={`m-nav-industry-${s.slug}`}>
              {s.label}
            </Link>
          ))}
          <Link to="/process" data-testid="m-nav-process">Process</Link>
          <Link
            to="/contact"
            data-testid="m-nav-contact"
            style={{ background: "var(--carbon-black)", color: "var(--bright-snow)", marginTop: 12, textAlign: "center" }}
          >
            Contact
          </Link>
        </div>
      )}
    </>
  );
}
